import { api } from '../../lib/api';
import type { ModelMapStandard } from '../../lib/leadRelay/modelMapStandard';
import type { LeadRouteFields } from '../../lib/leadRelay/routeRules';
import type {
  EditableLeadField,
  LeadAttempt,
  LeadFailureReason,
  LeadLogResponse,
  LeadProvider,
  LeadRouteDetail,
  LeadRouteListItem,
  LeadSubmission,
  LeadSubmissionDetail,
  LeadSubmissionKind,
  LeadSubmissionStatus,
  LeadSummaryResponse,
} from '../../types/leadRelay';

// Meta Lead → VirtualYard relay — the CRM's API (docs/lead-relay.md §10).
// ⚠️ Resend and Send test lead call OUR server, which calls the relay. Nothing
// in the browser ever talks to VirtualYard or to the relay directly.

export interface RelayOutcome {
  submissionId: string;
  status: LeadSubmissionStatus;
  failureReason: LeadFailureReason | null;
  lastAttempt: LeadAttempt | null;
}

export interface RouteSaveResult {
  id: string;
  /** Saved — but worth a look (e.g. a token that differs from its siblings). */
  warnings: string[];
}

export async function getLeadRelayStatus(): Promise<{ relayConfigured: boolean }> {
  return api('/api/lead-relay/status');
}

export async function listLeadRouteNames(): Promise<Array<{ id: string; name: string; testMode: boolean }>> {
  const { routes } = await api<{ routes: Array<{ id: string; name: string; testMode: boolean }> }>(
    '/api/lead-relay/route-names',
  );
  return routes;
}

export async function listLeadRoutes(): Promise<LeadRouteListItem[]> {
  const { routes } = await api<{ routes: LeadRouteListItem[] }>('/api/lead-relay/routes');
  return routes;
}

export async function getLeadRoute(id: string): Promise<LeadRouteDetail> {
  return api(`/api/lead-relay/routes/${encodeURIComponent(id)}`);
}

export async function createLeadRoute(id: string, fields: LeadRouteFields): Promise<RouteSaveResult> {
  return api('/api/lead-relay/routes', { method: 'POST', body: JSON.stringify({ id, fields }) });
}

export async function updateLeadRoute(id: string, fields: LeadRouteFields): Promise<RouteSaveResult> {
  return api(`/api/lead-relay/routes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(fields),
  });
}

/** The shared model maps a VirtualYard route can inherit from (§25). Admin-only. */
export async function listModelMapStandards(): Promise<ModelMapStandard[]> {
  const { standards } = await api<{ standards: ModelMapStandard[] }>('/api/lead-relay/model-map-standards');
  return standards;
}

export async function sendTestLead(routeId: string, alsoSendToSamMobile?: string): Promise<RelayOutcome> {
  const mobile = alsoSendToSamMobile?.trim();
  return api(`/api/lead-relay/routes/${encodeURIComponent(routeId)}/test`, {
    method: 'POST',
    ...(mobile ? { body: JSON.stringify({ alsoSendToSamMobile: mobile }) } : {}),
  });
}

export interface LeadLogQuery {
  routeId?: string;
  statuses?: LeadSubmissionStatus[];
  kind: LeadSubmissionKind | 'all';
  fromIso?: string;
  toIso?: string;
}

export async function listLeadLog(query: LeadLogQuery): Promise<LeadLogResponse> {
  const params = new URLSearchParams();
  if (query.routeId) params.set('routeId', query.routeId);
  if (query.statuses && query.statuses.length > 0) params.set('statuses', query.statuses.join(','));
  params.set('kind', query.kind);
  if (query.fromIso) params.set('from', query.fromIso);
  if (query.toIso) params.set('to', query.toIso);
  return api(`/api/lead-relay/submissions?${params.toString()}`);
}

export async function getLeadSubmission(id: string): Promise<LeadSubmissionDetail> {
  return api(`/api/lead-relay/submissions/${encodeURIComponent(id)}`);
}

/** Assign an unassigned lead to a route AND send it — one action, through the relay. */
export async function assignLead(id: string, routeId: string, provider: LeadProvider | null = null): Promise<RelayOutcome> {
  return api(`/api/lead-relay/submissions/${encodeURIComponent(id)}/assign`, {
    method: 'POST',
    // `provider` only when a person asserted it (the picker) — never otherwise.
    body: JSON.stringify(provider ? { routeId, provider } : { routeId }),
  });
}

export async function resendLead(id: string): Promise<RelayOutcome> {
  return api(`/api/lead-relay/submissions/${encodeURIComponent(id)}/resend`, { method: 'POST' });
}

/** §20 — written by OUR server (it touches no form), audited. Failed or unassigned; an unassigned lead needs a note. */
export async function markLeadKeyedManually(id: string, note: string): Promise<LeadSubmission> {
  const { submission } = await api<{ submission: LeadSubmission }>(
    `/api/lead-relay/submissions/${encodeURIComponent(id)}/keyed-manually`,
    { method: 'POST', body: JSON.stringify({ note }) },
  );
  return submission;
}

/**
 * Change a FAILED lead's own answers. Written by OUR server, audited (field names
 * only). Sends nothing — Resend is a separate action. Any staff.
 */
export async function editLeadInput(
  id: string,
  fields: Partial<Record<EditableLeadField, string>>,
): Promise<LeadSubmission> {
  const { submission } = await api<{ submission: LeadSubmission }>(
    `/api/lead-relay/submissions/${encodeURIComponent(id)}/input`,
    { method: 'POST', body: JSON.stringify({ fields }) },
  );
  return submission;
}

export async function getLeadSummary(accountId: string, sinceIso: string): Promise<LeadSummaryResponse> {
  const params = new URLSearchParams({ accountId, since: sinceIso });
  return api(`/api/lead-relay/summary?${params.toString()}`);
}
